import React from 'react';
import * as yup from 'yup';
import useAwaitedForm from '../useAwaitedForm';
import { getBusinessCurrency } from '../../helpers/businesses';
import useUser from '../global/useUser';
import CashDiscountFormRow from '../../components/payments/CashDiscountFormRow';
import useCashDiscount from '../payments/useCashDiscount';

const depositPriceFormSchema = yup.object({
  depositPrice: yup
    .number()
    .transform((v, orig) =>
      orig === '' || orig == null ? undefined : Number(v),
    )
    .min(0.01, 'Deposit must be greater than 0')
    .required('Deposit is required'),
});

export type DepositPriceFormValues = yup.InferType<
  typeof depositPriceFormSchema
>;

interface ShowDepositPriceFormOptions {
  initialPrice: number;
  /** Selected payment method — drives terminal vs online rate/min fee in the cd row. */
  paymentMethod?: string | null;
}

export function useDepositPriceFormModal() {
  const onAwaitForm = useAwaitedForm();
  const { user } = useUser();
  const { attachedInputSx } = useCashDiscount();

  return React.useCallback(
    ({
      initialPrice,
      paymentMethod,
    }: ShowDepositPriceFormOptions): Promise<DepositPriceFormValues | null> =>
      onAwaitForm<DepositPriceFormValues>({
        allowUntouched: true,
        form: {
          title: 'Edit deposit',
          initialValues: { depositPrice: initialPrice },
          validationSchema: depositPriceFormSchema,
          submitButtonText: 'Save',
          inputRows: values => {
            const priceValue = Number(values.depositPrice) || 0;
            return [
              [
                {
                  inputType: 'number',
                  name: 'depositPrice',
                  label: 'Deposit price',
                  min: 0,
                  step: 0.01,
                  autoFocus: true,
                  suffix: getBusinessCurrency(user?.business),
                  inputSx: priceValue > 0 ? attachedInputSx : undefined,
                },
              ],
              [
                {
                  inputType: 'node',
                  isVisible: !!attachedInputSx && priceValue > 0,
                  node: React.createElement(CashDiscountFormRow, {
                    price: {
                      type: 'FIXED',
                      amount: priceValue,
                    },
                    variant: 'attached',
                    paymentMethod,
                  }),
                },
              ],
            ];
          },
        },
      }),
    [onAwaitForm, user, attachedInputSx],
  );
}
